/**
 * Compresión de imágenes en el cliente, antes de subir un comprobante.
 *
 * Existe por dos razones distintas, y las dos importan:
 *
 * 1. **Datos móviles.** El empleado captura el gasto en la tienda, con señal mala.
 *    Mandar los 4 MB que salen de la cámara falla o tarda una eternidad.
 *
 * 2. **HEIC de iPhone.** Es el formato default de la cámara de Apple y el backend
 *    no lo soporta (ImageSharp no trae decodificador). Al pasar por canvas y
 *    re-encodear a JPEG, el servidor nunca ve un HEIC.
 *
 * Sobre el punto 2: **no se puede dar por resuelto con "el canvas lo convierte"**.
 * Depende del sistema operativo, no del navegador:
 *
 * | Escenario                              | ¿Decodifica? |
 * |----------------------------------------|--------------|
 * | iOS, foto tomada con capture=environment | Safari suele entregar JPEG ya convertido |
 * | iOS, foto elegida del carrete            | Suele convertirse, pero no está garantizado |
 * | macOS Safari                             | Sí (decodificador del SO) |
 * | Windows / Android, archivo .heic         | **No** — no hay decodificador |
 *
 * Por eso `compressImage` devuelve un resultado explícito en vez de lanzar o de
 * caer de vuelta al archivo crudo: subir el HEIC original sólo cambiaría el error
 * de lugar (un 400 tardío del servidor en vez de un mensaje claro aquí).
 */

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;

export type CompressionResult =
  | { ok: true; file: File }
  | { ok: false; reason: 'decode-failed' };

/** Los PDF no pasan por canvas: se mandan tal cual. */
export const isPdf = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/**
 * Redimensiona a 1600px de lado largo y re-encodea a JPEG.
 *
 * Nunca lanza: un archivo que el browser no puede decodificar devuelve
 * `{ ok: false }` para que el caller muestre un mensaje accionable y siga con el
 * resto del lote.
 */
export async function compressImage(file: File): Promise<CompressionResult> {
  if (isPdf(file)) {
    return { ok: true, file };
  }

  let objectUrl: string | undefined;

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      objectUrl = URL.createObjectURL(file);
      img.onload = () => resolve(img);
      // onerror es el camino del HEIC en Windows/Android: el browser simplemente
      // no tiene con qué decodificarlo.
      img.onerror = () => reject(new Error('decode-failed'));
      img.src = objectUrl;
    });

    const scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
    const width = Math.round(image.width * scale);
    const height = Math.round(image.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return { ok: false, reason: 'decode-failed' };

    ctx.drawImage(image, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
    });

    // Un blob vacío es el otro síntoma de "el canvas no pudo con esto": algunos
    // navegadores no fallan en onerror pero producen 0 bytes.
    if (!blob || blob.size === 0) {
      return { ok: false, reason: 'decode-failed' };
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'comprobante';
    return {
      ok: true,
      file: new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }),
    };
  } catch {
    return { ok: false, reason: 'decode-failed' };
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

export const HEIC_HINT =
  'No pudimos procesar esta imagen (probablemente HEIC de iPhone). ' +
  'Vuelve a tomar la foto desde la app, o conviértela a JPG.';
